'use client';

import { hasUpcomingOnlinePaymentSlot } from '@website/utils/slotPaymentCheck';

import { useActivitiesByProfessionalId } from './useActivitiesByProfessional';
import { useCurrentUser } from './useCurrentUser';
import { useStripeAccountStatus } from './useStripeAccountStatus';

/**
 * Returns true when the professional's Stripe account requires attention.
 *
 * Rules (mirrors ProfileSidebar `showPaymentsBadge`):
 * - Incomplete onboarding: only flagged once the professional has at least one
 *   slot configured to accept online payments. Until then, the payments page
 *   has nothing to unlock for them and surfacing a warning is noise.
 * - `requiresAction` (post-onboarding Stripe request): only flagged when the
 *   professional already has a Stripe account. The backend defaults
 *   `requiresAction: true` for accounts that haven't been created yet (see
 *   `getStripeAccountStatus.ts`), and acting on that flag pre-creation would
 *   surface a phantom "1" on the Settings tab for pros without any online-
 *   payment slots configured — the same noise the onboarding rule above
 *   tries to avoid.
 */
export function useStripeActionRequired(): boolean {
  const { data: user } = useCurrentUser();
  const { data: stripeStatus } = useStripeAccountStatus(user?.isProfessional ? user.id : undefined);
  const { data: activitiesData } = useActivitiesByProfessionalId(
    user?.isProfessional ? user.id : undefined,
    '',
    undefined
  );

  if (!stripeStatus) return false;

  // Only *upcoming* online-payment slots count — a purely-past online slot can
  // no longer take an online booking, so it must not drive the payments badge.
  const hasOnlinePaymentSlots = hasUpcomingOnlinePaymentSlot(activitiesData);

  return (
    (!stripeStatus.onboardingComplete && hasOnlinePaymentSlots) ||
    (stripeStatus.hasStripeAccount && stripeStatus.requiresAction)
  );
}
